import * as React from 'react';
import isTargetElementTotallyScrolled from '../utils/isTargetElementTotallyScrolled';
import { isIOS } from 'mini-device-utils/lib/utils/isIOS';
import { trace } from 'owa-trace';

// Target element type
type TargetElement = HTMLElement;

// List of target elements in the DOM for which scroll is enabled
let targetElements: Array<TargetElement> = [];

// Boolean to keep track if any document listener is added to document
let documentListenerAdded: boolean = false;

// We save the previous body overflow value in this variable and
// use to it to set back the previous value once we again enable body scroll.
let previousBodyOverflowSetting: string | null = null;

/**
 * Higher order component which prevents scroll when a modal is shown
 * @param WrappedComponent Modal component (wrapped in React.ForwardRef for a functional component)
 * Body scroll is disabled when this component is mounted and enabled when unmounted.
 */

export default function PreventModalScroll<P>(
    WrappedComponent: React.ComponentClass<P> | React.FunctionComponent<P>,
    getScrollableTargetElement: (component: React.ComponentClass<P>) => HTMLDivElement
): React.ComponentClass<P> {
    return class extends React.Component<P, {}> {
        private wrappedComponentRef = React.createRef<React.ComponentClass<P>>();
        private scrollableTargetElement: HTMLElement = null;
        private isIosDevice: boolean = isIOS();
        private initialClientY: number = -1;
        private initialClientX: number = -1;

        render() {
            return <WrappedComponent {...this.props} ref={this.wrappedComponentRef} />;
        }

        componentDidMount() {
            try {
                this.scrollableTargetElement = getScrollableTargetElement(
                    this.wrappedComponentRef?.current
                );
                this.disableScrollOnBody(this.scrollableTargetElement);
            } catch (error) {
                trace.warn(`Failed to disableScrollOnBody: ${error}`);
            }
        }

        componentWillUnmount() {
            try {
                this.enableScrollOnBody(this.scrollableTargetElement);
            } catch (error) {
                this.clearAllBodyScrollLocks();
                trace.warn(`Failed to enableScrollOnBody: ${error}`);
            }
        }

        setOverflowHidden = () => {
            // Set async overflow
            window.requestAnimationFrame &&
                window.requestAnimationFrame(() => {
                    // If previousBodyOverflowSetting is already set, don't set it again.
                    if (previousBodyOverflowSetting === null) {
                        previousBodyOverflowSetting = document.body.style.overflow;
                        document.body.style.overflow = 'hidden';
                    }
                });
        };

        addToTargetList = (targetElement: TargetElement) => {
            targetElements = [...targetElements, targetElement];
        };

        handleScroll = (event: TouchEvent, targetElement: HTMLElement): boolean => {
            const clientY = event.targetTouches[0].clientY - this.initialClientY;
            const clientX = event.targetTouches[0].clientX - this.initialClientX;

            const isVerticalScrollMoreThanHorizontal = Math.abs(clientX) < Math.abs(clientY);

            // element is at the top or bottom of its scroll
            if (
                ((targetElement && targetElement.scrollTop === 0 && clientY > 0) ||
                    (isTargetElementTotallyScrolled(targetElement) && clientY < 0)) &&
                isVerticalScrollMoreThanHorizontal
            ) {
                return this.preventDefault(event);
            }

            // Stop the event from bubbling up but don't prevent default
            event.stopPropagation();
            return true;
        };

        preventDefault = (event: TouchEvent): boolean => {
            if (event.preventDefault) {
                event.preventDefault();
            }
            return false;
        };

        restoreOverflowSetting = () => {
            // Set async overflow
            window.requestAnimationFrame &&
                window.requestAnimationFrame(() => {
                    // No need to set if previousBodyOverflowSetting is undefined
                    if (previousBodyOverflowSetting !== null) {
                        document.body.style.overflow = previousBodyOverflowSetting;
                        previousBodyOverflowSetting = null;
                    }
                });
        };

        /**
         * Disables scroll on body
         */
        disableScrollOnBody = (currentTargetElement: HTMLElement): void => {
            if (this.isIosDevice) {
                // TargetElement must be provided & disableBodyScroll must not have been called on this targetElement before.
                if (!currentTargetElement) {
                    trace.warn(
                        'scrollableItemRef must be provided in modal component for disable scroll to work on IOS devices!'
                    );
                    return;
                }

                if (!targetElements.some(element => element === currentTargetElement)) {
                    // Add targetElement in locks for tracking
                    this.addToTargetList(currentTargetElement);

                    currentTargetElement.ontouchstart = (event: TouchEvent) => {
                        if (event.targetTouches.length === 1) {
                            this.initialClientY = event.targetTouches[0].clientY;
                            this.initialClientX = event.targetTouches[0].clientX;
                        }
                    };

                    currentTargetElement.ontouchmove = (event: TouchEvent) => {
                        if (event.targetTouches.length === 1) {
                            this.handleScroll(event, currentTargetElement);
                        }
                    };

                    // Add if no document listener is added previously
                    if (!documentListenerAdded) {
                        document.addEventListener('touchmove', this.preventDefault);
                        documentListenerAdded = true;
                    }
                }
            } else {
                this.setOverflowHidden();
                this.addToTargetList(currentTargetElement);
            }
        };

        /**
         * Enabled scroll on body
         */
        enableScrollOnBody = (currentTargetElement: HTMLElement): void => {
            if (this.isIosDevice) {
                // Handle iOS Device
                if (!currentTargetElement) {
                    trace.warn(
                        'scrollableItemRef must be provided in modal component for disable scroll to work on IOS devices!'
                    );
                    return;
                }

                currentTargetElement.ontouchstart = null;
                currentTargetElement.ontouchmove = null;

                targetElements = targetElements.filter(element => element !== currentTargetElement);

                // If there are no target elements present, remove document listener if present
                // If there are other target elements present means they would need the listener.
                if (documentListenerAdded && targetElements.length === 0) {
                    document.removeEventListener('touchmove', this.preventDefault);
                    documentListenerAdded = false;
                }
            } else {
                // Handle other devices
                if (targetElements.length === 1 && targetElements[0] === currentTargetElement) {
                    // Current target element is the only one left
                    this.restoreOverflowSetting();
                    targetElements = [];
                } else {
                    // Filter out current target element
                    targetElements = targetElements.filter(
                        element => element !== currentTargetElement
                    );
                }
            }
        };

        clearAllBodyScrollLocks = (): void => {
            if (this.isIosDevice) {
                targetElements.forEach((targetElement: TargetElement) => {
                    targetElement.ontouchstart = null;
                    targetElement.ontouchmove = null;
                });

                if (documentListenerAdded) {
                    document.removeEventListener('touchmove', this.preventDefault);
                    documentListenerAdded = false;
                }

                targetElements = [];
                this.initialClientY = -1;
            } else {
                this.restoreOverflowSetting();
                targetElements = [];
            }
        };
    };
}
